// src/lib/quizIdbHelper.ts
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Quiz } from '../types';
import { logger } from '../utils/logger';

const DB_NAME = 'quiz-app-db';
const DB_VERSION = 1;
const STORE_NAME = 'quizzes';
const CACHE_EXPIRY_MONTHS = 1; // キャッシュ有効期限（1ヶ月）

// Quizオブジェクトにキャッシュ期限を追加した型
type CachedQuiz = Quiz & {
    expiresAt: number;
};

// データベースのスキーマ定義
interface QuizAppDB extends DBSchema {
    [STORE_NAME]: {
        key: string;
        value: CachedQuiz;
    };
}

// データベース接続を管理する変数
let dbPromise: Promise<IDBPDatabase<QuizAppDB>> | null = null;

/**
 * データベースへの接続を初期化し、シングルトンとして管理する
 */
const getDb = (): Promise<IDBPDatabase<QuizAppDB>> => {
    if (!dbPromise) {
        dbPromise = openDB<QuizAppDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                // 'quizzes' オブジェクトストアを作成 (キーはクイズID)
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            },
        });
    }
    return dbPromise;
};

/**
 * キャッシュの有効期限を計算する
 * @returns {number} 1ヶ月後のタイムスタンプ (ミリ秒)
 */
const getExpiryTimestamp = (): number => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + CACHE_EXPIRY_MONTHS);
    return expiryDate.getTime();
};


/**
 * クイズをIndexedDBに保存（追加または更新）します
 * @param quiz - 保存するクイズデータ
 */
export const setQuizToDb = async (quiz: Quiz): Promise<void> => {
    try {
        const db = await getDb();
        const cachedQuiz: CachedQuiz = {
            ...quiz,
            expiresAt: getExpiryTimestamp(),
        };
        await db.put(STORE_NAME, cachedQuiz);
        logger.log(`Quiz ${quiz.id} saved to IndexedDB.`);
    } catch (error) {
        logger.error(`Failed to save quiz ${quiz.id} to IndexedDB:`, error);
    }
};

/**
 * IndexedDBからクイズを取得します。キャッシュ期限もチェックします。
 * @param quizId - 取得するクイズのID
 * @returns {Promise<Quiz | null>} クイズデータ、または存在しない/期限切れの場合はnull
 */
export const getQuizFromDb = async (quizId: string): Promise<Quiz | null> => {
    try {
        const db = await getDb();
        const cachedQuiz = await db.get(STORE_NAME, quizId);

        if (!cachedQuiz) {
            return null;
        }

        // キャッシュの有効期限をチェック
        if (Date.now() > cachedQuiz.expiresAt) {
            logger.log(`Cache for quiz ${quizId} expired. Deleting from IndexedDB.`);
            // 期限切れのデータを非同期で削除
            await deleteQuizFromDb(quizId);
            return null;
        }
        
        // expiresAtを除いた純粋なQuizオブジェクトを返す
        const { expiresAt, ...quizData } = cachedQuiz;
        return quizData as Quiz;

    } catch (error) {
        logger.error(`Failed to get quiz ${quizId} from IndexedDB:`, error);
        return null;
    }
};

/**
 * IndexedDBからクイズを削除します
 * @param quizId - 削除するクイズのID
 */
export const deleteQuizFromDb = async (quizId: string): Promise<void> => {
    try {
        const db = await getDb();
        await db.delete(STORE_NAME, quizId);
        logger.log(`Quiz ${quizId} deleted from IndexedDB.`);
    } catch (error) {
        logger.error(`Failed to delete quiz ${quizId} from IndexedDB:`, error);
    }
};